import React, { useCallback, useEffect, useState } from "react";
import AnnaFaceSettings from "../components/AnnaFaceSettings";
import DashLoading from "../components/DashLoading";
import { Link } from "react-router-dom";
import { useIstToday } from "../lib/istDate";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { userPages } from "../lib/pages";
import { istDateTime } from "../components/AnnaTicketSlip";
import { DashHero, DashCaption, Section, Tile, TileGrid, DashTable, useDashImage, n } from "../components/TempleDash";
import { Soup, CheckCircle2, Hourglass, Timer, Users, Baby, ScanLine, FileBarChart, Ticket, Crown, Heart } from "lucide-react";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid, Label } from "recharts";

const COLORS = ["#0369A1", "#BE185D", "#D97706", "#475569"];
const TYPE_CHIP = { GENERAL: "bg-amber-100 text-amber-900", KALYANAM: "bg-pink-100 text-pink-900", VIP: "bg-yellow-200 text-yellow-900" };
const AX = { fontSize: 11, fill: "#64748B" };
const GRID = <CartesianGrid strokeDasharray="3 3" stroke="#F0E6D2" vertical={false} />;

const Chart = ({ title, children, className = "" }) => <Section title={title} className={className} bodyClass="p-3 h-64">{children}</Section>;
const TYPE_ICON = { GENERAL: Ticket, KALYANAM: Heart, VIP: Crown };
const TYPE_TONE = { GENERAL: "yellow", KALYANAM: "red", VIP: "purple" };

export default function AnnaDashboard() {
  const { user } = useAuth();
  const pages = userPages(user);
  const [temples, setTemples] = useState([]);
  const [templeId, setTempleId] = useState("");
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => { api.get("/meta/form-data").then((r) => setTemples(r.data.temples || [])).catch(() => {}); }, []);
  const load = useCallback(() => {
    api.get("/anna/dashboard", { params: templeId ? { temple_id: templeId } : {} }).then((r) => { setD(r.data); setError(null); }).catch((e) => setError(e));
  }, [templeId]);
  const istDay = useIstToday(); // new day at 00:00 IST: reload today's figures at once
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load, istDay]);

  const temple = temples.find((t) => t.temple_id === (templeId || user.temple_id));
  const t = d?.today;
  const { url } = useDashImage("ANNA", templeId, user);

  return (
    <div className="space-y-5" data-testid="anna-dashboard">
      {d ? (
        <DashHero image={url} testid="anna-hero"
          left={[
            { icon: Soup, label: "Tickets Today", value: n(t.total), testid: "anna-dash-total" },
            { icon: CheckCircle2, label: "Verified (Served)", value: n(t.verified), testid: "anna-dash-verified" },
            { icon: Hourglass, label: "Yet to Verify", value: n(t.pending), testid: "anna-dash-pending" },
            { icon: Timer, label: "Avg Wait", value: `${t.avg_wait_min} min`, testid: "anna-dash-wait" },
          ]}
          right={[
            { icon: Users, label: "Male", value: n(t.M), testid: "anna-dash-m" },
            { icon: Users, label: "Female", value: n(t.F), testid: "anna-dash-f" },
            { icon: Baby, label: "Children", value: n(t.C), testid: "anna-dash-c" },
            { icon: Users, label: "Others", value: n(t.O), testid: "anna-dash-o" },
          ]} />
      ) : <DashHero image={url} left={[]} right={[]} />}

      <DashCaption temple={temple} title="Annaprasadam Dashboard">
        <div className="flex flex-wrap gap-2">
          {user.role === "SUPER_ADMIN" && (
            <select value={templeId} onChange={(e) => setTempleId(e.target.value)} className="h-10 rounded-lg border border-[#E5D9C5] px-3 text-sm bg-white" data-testid="anna-dash-temple">
              <option value="">All temples</option>
              {temples.map((x) => <option key={x.temple_id} value={x.temple_id}>{x.temple_id} · {x.name}</option>)}
            </select>
          )}
          {pages.includes("ANNA_ISSUE") && <Link to="/anna/issue" className="h-10 px-4 rounded-lg bg-maroon-700 text-white text-sm font-semibold inline-flex items-center gap-1.5"><Soup className="w-4 h-4" /> Issue</Link>}
          {pages.includes("ANNA_VERIFY") && <Link to="/anna/verify" className="h-10 px-4 rounded-lg bg-green-700 text-white text-sm font-semibold inline-flex items-center gap-1.5"><ScanLine className="w-4 h-4" /> Verify</Link>}
          {pages.includes("ANNA_REPORTS") && <Link to="/anna/reports" className="h-10 px-4 rounded-lg border border-[#E5D9C5] bg-white text-maroon-800 text-sm font-semibold inline-flex items-center gap-1.5"><FileBarChart className="w-4 h-4" /> Reports</Link>}
        </div>

      </DashCaption>

      {["SUPER_ADMIN", "TEMPLE_ADMIN"].includes(user.role) && <AnnaFaceSettings templeId={templeId || user.temple_id} />}

      {!d ? <DashLoading error={error} onRetry={() => { setError(null); load(); }} testid="anna-dash-loading" /> : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <Section title="Today's Service">
              <TileGrid cols={3}>
                <Tile icon={Soup} label="Issued" value={n(t.total)} tone="blue" />
                <Tile icon={CheckCircle2} label="Served" value={n(t.verified)} tone="green" />
                <Tile icon={Hourglass} label="Waiting" value={n(t.pending)} tone="red" />
              </TileGrid>
            </Section>
            <Section title="Ticket Types (Today)">
              <TileGrid cols={3}>
                {d.type_wise.slice(0, 3).map((x) => (
                  <Tile key={x.name} icon={TYPE_ICON[x.code] || Ticket} label={x.name} value={n(x.issued)} note={`${n(x.verified)} served`} tone={TYPE_TONE[x.code] || "yellow"} />
                ))}
              </TileGrid>
            </Section>
            <Section title="Devotees (Today)">
              <TileGrid cols={4}>
                <Tile icon={Users} label="Male" value={n(t.M)} tone="blue" />
                <Tile icon={Users} label="Female" value={n(t.F)} tone="red" />
                <Tile icon={Baby} label="Children" value={n(t.C)} tone="yellow" />
                <Tile icon={Users} label="Others" value={n(t.O)} tone="slate" />
              </TileGrid>
            </Section>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Chart title="Hourly Tickets (Today)" className="lg:col-span-2">
              <ResponsiveContainer>
                <BarChart data={d.hourly} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                  {GRID}
                  <XAxis dataKey="hour" tick={AX} tickLine={false} minTickGap={8} />
                  <YAxis allowDecimals={false} tick={AX} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "#FDF6E3" }} />
                  <Bar dataKey="issued" name="Tickets" fill="#800020" radius={[4, 4, 0, 0]} maxBarSize={34} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            </Chart>
            <Chart title="Category-wise (Today)">
              {t.total === 0 ? <p className="text-sm text-slate-400 text-center pt-20">No tickets yet today</p> : (
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={d.category_wise.filter((x) => x.value > 0)} dataKey="value" nameKey="name" innerRadius={58} outerRadius={88} paddingAngle={2} isAnimationActive={false}>
                      {d.category_wise.filter((x) => x.value > 0).map((x) => <Cell key={x.name} fill={COLORS[d.category_wise.findIndex((y) => y.name === x.name)]} />)}
                      <Label value={`${t.total} tickets`} position="center" style={{ fontSize: 15, fontWeight: 700, fill: "#5B0016" }} />
                    </Pie>
                    <Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Chart>
            <Chart title="Ticket Type-wise (Today)">
              <ResponsiveContainer>
                <BarChart data={d.type_wise} margin={{ top: 8, right: 8, left: -18, bottom: 0 }} barGap={4}>
                  {GRID}
                  <XAxis dataKey="name" tick={AX} tickLine={false} />
                  <YAxis allowDecimals={false} tick={AX} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "#FDF6E3" }} /><Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="issued" name="Issued" fill="#D4AF37" radius={[4, 4, 0, 0]} maxBarSize={38} isAnimationActive={false} />
                  <Bar dataKey="verified" name="Verified" fill="#15803D" radius={[4, 4, 0, 0]} maxBarSize={38} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            </Chart>
            <Chart title="Last 7 Days" className="lg:col-span-2">
              <ResponsiveContainer>
                <BarChart data={d.last7} margin={{ top: 8, right: 8, left: -18, bottom: 0 }} barGap={4}>
                  {GRID}
                  <XAxis dataKey="day" tick={AX} tickLine={false} />
                  <YAxis allowDecimals={false} tick={AX} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "#FDF6E3" }} /><Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="issued" name="Issued" fill="#800020" radius={[4, 4, 0, 0]} maxBarSize={30} isAnimationActive={false} />
                  <Bar dataKey="verified" name="Verified" fill="#15803D" radius={[4, 4, 0, 0]} maxBarSize={30} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            </Chart>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
            <Section title="Counter-wise (Today)">
              <DashTable testid="anna-dash-counters" head={["Counter", "Issued", "Verified"]} empty="No tickets yet today"
                rows={d.counter_wise.map((c) => [c.name, n(c.issued), <span key="v" className="text-[#1F9D55]">{n(c.verified)}</span>])}
                foot={d.counter_wise.length > 1 ? ["TOTAL", n(t.total), n(t.verified)] : null} />
            </Section>
            <Section title="Latest Tickets" className="lg:col-span-2" bodyClass="p-3 overflow-x-auto"
              right={pages.includes("ANNA_REPORTS") && <Link to="/anna/reports" className="text-xs font-semibold text-white/90 hover:underline">View all →</Link>}>
                <table className="w-full text-sm" data-testid="anna-dash-recent">
                  <thead><tr className="text-left text-gold-700 text-[11px] uppercase tracking-wider border-b border-[#E5D9C5]">
                    <th className="py-2">Ticket No</th><th>Type</th><th>Category</th><th>Counter</th><th>Status</th><th className="text-right">Time</th></tr></thead>
                  <tbody>
                    {d.recent.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-slate-400">No tickets yet today</td></tr>}
                    {d.recent.map((r) => (
                      <tr key={r.id || r.ticket_no} className="border-b border-[#F5EDE0]">
                        <td className="py-2 pr-3 font-mono font-semibold text-maroon-800 whitespace-nowrap">{r.ticket_no}</td>
                        <td className="pr-3"><span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${TYPE_CHIP[r.ticket_type] || "bg-slate-100"}`}>{r.ticket_type_name?.replace(" Ticket", "")}</span></td>
                        <td className="pr-3">{r.category_name}</td>
                        <td className="pr-3 text-slate-600 whitespace-nowrap">{r.counter_name || "-"}</td>
                        <td className="pr-3"><span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${r.status === "VERIFIED" ? "bg-green-100 text-green-800" : "bg-orange-100 text-orange-800"}`}>{r.status === "VERIFIED" ? "Verified" : "Issued"}</span></td>
                        <td className="text-right text-slate-500 whitespace-nowrap">{istDateTime(r.created_at).split(", ")[1]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
            </Section>
          </div>
        </>
      )}
    </div>
  );
}
